import React, { useState } from 'react';
import { useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import Flex from '@atlaskit/primitives/components/flex';
import Text from '@atlaskit/primitives/text';
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';
import IconButton from '@atlaskit/button/icon/button';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import DeleteIcon from '@atlaskit/icon/core/delete';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { fg } from '@confluence/feature-gating';

import { i18n } from './inlineCommentsi18n.ts';

const expandedContentStyles = xcss({
	marginTop: 'space.150',
});

interface DanglingContentProps {
	annotatedText?: string | null;
}

export const DanglingContent: React.FC<DanglingContentProps> = ({ annotatedText }) => {
	const { formatMessage } = useIntl();
	const [isDanglingContentExpanded, setIsDanglingContentExpanded] = useState(false);

	const getContentMessage = () => {
		if (fg('confluence_frontend_new_dangling_comments_ux')) {
			return formatMessage(i18n.thisContentWasDeleted);
		}
		return formatMessage(i18n.contentDeleted);
	};

	return (
		<Box>
			<Flex gap="space.100" alignItems="center" justifyContent="space-between">
				<Flex gap="space.100" alignItems="center">
					{!fg('confluence_frontend_new_dangling_comments_ux') && (
						<DeleteIcon label="" color={token('color.icon.subtlest')} />
					)}
					<Text
						size="small"
						maxLines={3}
						color={
							fg('confluence_frontend_new_dangling_comments_ux')
								? 'color.text'
								: 'color.text.subtle'
						}
						weight={fg('confluence_frontend_new_dangling_comments_ux') ? 'bold' : 'regular'}
					>
						{getContentMessage()}
					</Text>
				</Flex>
				{fg('confluence_frontend_new_dangling_comments_ux') && (
					<Tooltip
						content={
							isDanglingContentExpanded
								? formatMessage(i18n.collapseDeletedContent)
								: formatMessage(i18n.viewDeletedContent)
						}
					>
						<IconButton
							appearance="subtle"
							icon={isDanglingContentExpanded ? ChevronUpIcon : ChevronDownIcon}
							label={
								isDanglingContentExpanded
									? formatMessage(i18n.collapseDeletedContent)
									: formatMessage(i18n.viewDeletedContent)
							}
							onClick={() => setIsDanglingContentExpanded(!isDanglingContentExpanded)}
							testId={
								isDanglingContentExpanded
									? 'dangling-content-toggle-collapse'
									: 'dangling-content-toggle-expand'
							}
							spacing="compact"
						/>
					</Tooltip>
				)}
			</Flex>
			{isDanglingContentExpanded && fg('confluence_frontend_new_dangling_comments_ux') && (
				<Box xcss={expandedContentStyles}>
					<Text size="small" color="color.text.subtlest" maxLines={3}>
						{annotatedText}
					</Text>
				</Box>
			)}
		</Box>
	);
};
